import { ref } from "vue";
import { useStore } from "vuex";

import { defineStore } from "pinia";

import { Entity } from "@/enums/entityProfiles";
import DashboardApiService from "@/services/modules/businesses";
import profilesApiService from "@/services/modules/profiles";

import type { IApplication } from "@/models/applications";
import type {
  BusinessEntityProfileRecord,
  IBusinessEntityProfile,
} from "@/models/businesses";
import type { IResponseMeta } from "@/models/common";
import type { CreateEntityProfilePayload } from "@/models/entityProfiles";
import type { Ref } from "vue";

interface UseBusinessesStore {
  businesses: Ref<IBusinessEntityProfile[]>;
  meta: Ref<IResponseMeta>;
  activeBusiness: Ref<IBusinessEntityProfile>;
  activeBusinessEntityProfile: Ref<IBusinessEntityProfile>;
  activeBusinessApplications: Ref<IApplication[]>;
  activeBusinessApplicationsMeta: Ref<IResponseMeta>;
  lastBusinessApplication: Ref<IApplication>;
  businessProfiles: Ref<BusinessEntityProfileRecord[]>;
  businessProfilesMeta: Ref<IResponseMeta>;
  getAllBusinesses(params: Record<string, unknown>): Promise<void>;
  getBusinessProfile(businessId: number): Promise<void>;
  loadBusinessProfiles(params: Record<string, unknown>): Promise<void>;
  loadBusinessProfile(profileId: string): Promise<IBusinessEntityProfile>;
  getActiveBusinessApplications(payload: {
    businessId: number;
    params: Record<string, unknown>;
  }): Promise<void>;
  getLastBusinessApplication(businessId: number): Promise<void>;
  createNewApplication(payload: IApplication): Promise<IApplication>;
  updateClientTrackingToken(payload: {
    profileId: string;
    client_token: string;
  }): Promise<void>;
  updateBusinessEntityProfile(params: {
    profileId: string;
    params: Record<string, unknown>;
  }): Promise<IBusinessEntityProfile>;
  createEntityProfile(
    payload: CreateEntityProfilePayload
  ): Promise<IBusinessEntityProfile>;
}

const useBusinessesStore = defineStore("businesses", (): UseBusinessesStore => {
  const { commit } = useStore();

  // TODO: Type assertions to be cleaned up through FLO-1505 ticket
  const businesses = ref<IBusinessEntityProfile[]>([]);
  const meta = ref<IResponseMeta>({} as IResponseMeta);
  const activeBusiness = ref<IBusinessEntityProfile>(
    {} as IBusinessEntityProfile
  );
  const activeBusinessEntityProfile = ref<IBusinessEntityProfile>(
    {} as IBusinessEntityProfile
  );
  const activeBusinessApplications = ref<IApplication[]>([]);
  const activeBusinessApplicationsMeta = ref<IResponseMeta>(
    {} as IResponseMeta
  );
  const lastBusinessApplication = ref<IApplication>({} as IApplication);
  const businessProfiles = ref<BusinessEntityProfileRecord[]>([]);
  const businessProfilesMeta = ref<IResponseMeta>({} as IResponseMeta);

  const getAllBusinesses = async (params: Record<string, unknown>) => {
    const data = await DashboardApiService.getBusinesses(params);

    data.data.forEach((business) => {
      if (business.funding_advisor) {
        business.funding_advisor.full_name = `${business.funding_advisor.first_name} ${business.funding_advisor.last_name}`;
      }
    });

    businesses.value = data.data;
    meta.value = data.meta;
  };

  const getBusinessProfile = async (businessId: number) => {
    const data = await DashboardApiService.getSingleBusiness(businessId);
    activeBusiness.value = data;
  };

  const loadBusinessProfiles = async (
    params: Record<string, unknown>
  ): Promise<void> => {
    const response = await profilesApiService.getProfilesByType(
      Entity.business,
      params
    );

    businessProfiles.value = response.data as BusinessEntityProfileRecord[];
    businessProfilesMeta.value = response.meta;
  };

  const loadBusinessProfile = async (profileId: string) => {
    const response = await profilesApiService.getSingleProfileByType(
      Entity.business,
      profileId
    );

    activeBusinessEntityProfile.value = response;
    return response;
  };

  const getActiveBusinessApplications = async (payload: {
    businessId: number;
    params: Record<string, unknown>;
  }) => {
    const data = await DashboardApiService.getBusinessApplications(
      payload.businessId,
      payload.params
    );
    activeBusinessApplications.value = data.data as unknown as IApplication[];
    activeBusinessApplicationsMeta.value = data.meta;
  };

  const getLastBusinessApplication = async (businessId: number) => {
    const data =
      await DashboardApiService.getLastBusinessApplication(businessId);

    commit("applications/setActive", data, { root: true });
    lastBusinessApplication.value = data;
  };

  const createNewApplication = async (payload: IApplication) => {
    const { id, ...newAppData } = payload;
    return await DashboardApiService.createNewApplication(id, newAppData);
  };

  const updateClientTrackingToken = async (payload: {
    profileId: string;
    client_token: string;
  }) => {
    const { client_tracking_token } =
      await profilesApiService.updateProfileByType(
        Entity.business,
        payload.profileId,
        { client_tracking_token: payload.client_token }
      );

    activeBusinessEntityProfile.value.client_tracking_token =
      client_tracking_token;
  };

  const updateBusinessEntityProfile = async ({
    profileId,
    params,
  }: {
    profileId: string;
    params: Record<string, unknown>;
  }) => {
    const response = await profilesApiService.updateBusinessEntityProfile(
      profileId,
      params
    );

    activeBusinessEntityProfile.value = response.data;
    return response.data;
  };

  const createEntityProfile = async (payload: CreateEntityProfilePayload) => {
    const response = await profilesApiService.createEntityProfile(payload);
    if (payload.type === Entity.business) {
      businesses.value.push(response.data);
    }
    if (payload.type === Entity.individual) {
      commit("profiles/addIndividualProfile", response.data, { root: true });
    }
    return response.data;
  };

  return {
    businesses,
    meta,
    activeBusiness,
    activeBusinessEntityProfile,
    activeBusinessApplications,
    activeBusinessApplicationsMeta,
    lastBusinessApplication,
    businessProfiles,
    businessProfilesMeta,
    getAllBusinesses,
    getBusinessProfile,
    loadBusinessProfiles,
    loadBusinessProfile,
    getActiveBusinessApplications,
    getLastBusinessApplication,
    createNewApplication,
    updateClientTrackingToken,
    updateBusinessEntityProfile,
    createEntityProfile,
  };
});

export default useBusinessesStore;
